export type SensorType = "pulse" | "soil_moisture" | "soil_water_tension" | "dendrometer";

export interface VariableDefinition {
  id: number;
  field: string;
  sensorType: SensorType;
  sensorDisplayName: string;
}

export const VARIABLE_DEFINITIONS: VariableDefinition[] = [
  { id: 0, field: "resistance", sensorType: "pulse", sensorDisplayName: "Pulse" },
  { id: 1, field: "temperature", sensorType: "pulse", sensorDisplayName: "Pulse" },
  { id: 2, field: "moisture_content", sensorType: "pulse", sensorDisplayName: "Pulse" },

  { id: 3, field: "soil_moisture", sensorType: "soil_moisture", sensorDisplayName: "FDR" },
  { id: 4, field: "temperature", sensorType: "soil_moisture", sensorDisplayName: "FDR" },
  {
    id: 10,
    field: "soil_water_tension_vg",
    sensorType: "soil_moisture",
    sensorDisplayName: "FDR",
  },

  {
    id: 5,
    field: "soil_water_tension",
    sensorType: "soil_water_tension",
    sensorDisplayName: "Watermark",
  },
  {
    id: 6,
    field: "temperature",
    sensorType: "soil_water_tension",
    sensorDisplayName: "Watermark",
  },
  {
    id: 11,
    field: "soil_moisture_vg",
    sensorType: "soil_water_tension",
    sensorDisplayName: "Watermark",
  },

  { id: 7, field: "diameter", sensorType: "dendrometer", sensorDisplayName: "Dendrometer" },
  {
    id: 8,
    field: "tree_water_deficit",
    sensorType: "dendrometer",
    sensorDisplayName: "Dendrometer",
  },
  {
    id: 9,
    field: "diameter_minus_start",
    sensorType: "dendrometer",
    sensorDisplayName: "Dendrometer",
  },
];

const SENSOR_TYPE_PROPERTY: Record<SensorType, string> = {
  pulse: "pulse_sensor",
  soil_moisture: "soil_moisture_sensor",
  soil_water_tension: "soil_water_tension_sensor",
  dendrometer: "dendrometer_sensor",
};

export const getVariableById = (id: number): VariableDefinition | undefined =>
  VARIABLE_DEFINITIONS.find((v) => v.id === id);

export const getVariablesByField = (field: string): VariableDefinition[] =>
  VARIABLE_DEFINITIONS.filter((v) => v.field === field);

/**
 * Returns a disambiguated display label.
 * For fields shared by multiple sensor types (e.g. temperature):
 *   "Temperature (Pulse)"
 * For unique fields:
 *   "Resistance"
 */
export const getVariableDisplayLabel = (
  variable: VariableDefinition,
  t: (key: string) => string
): string => {
  const duplicates = getVariablesByField(variable.field);
  if (duplicates.length > 1) {
    return `${t(variable.field)} (${variable.sensorDisplayName})`;
  }
  return t(variable.field);
};

// Variables that require van-Genuchten parameters
const VAN_GENUCHTEN_FIELDS = ["soil_water_tension_vg", "soil_moisture_vg"];

export const installationMatchesVariable = (
  installation: any,
  variable: VariableDefinition
): boolean => {
  const prop = SENSOR_TYPE_PROPERTY[variable.sensorType];
  const sensorMatches = installation?.sensor?.[prop] != null;
  if (!sensorMatches) return false;

  // For van-Genuchten variables, also check if the installation has VG params
  if (VAN_GENUCHTEN_FIELDS.includes(variable.field)) {
    return installation?.has_van_genuchten_params === true;
  }

  return true;
};

export const filterInstallationsForVariable = (
  installations: any[],
  variableId: number | null
): any[] => {
  if (variableId == null) return installations;
  const variable = getVariableById(variableId);
  if (!variable) return installations;
  return installations.filter((inst) => installationMatchesVariable(inst, variable));
};
